/**
 * ============================================================================
 * USER / TENANT PAGE: WhatsApp Broadcast Campaigns (Sheets 21, 22, 23, 24)
 * ============================================================================
 * Purpose:
 *   Tenant workspace dashboard for managing broadcast campaigns (`tbl_campaign_master`),
 *   viewing delivery metrics, dispatching existing campaigns, and opening delivery logs.
 *
 * Database Scope:
 *   Tenant Database (`digi_user_<id>`)
 * ============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { campaignService } from "../../services";
import Modal from "../../../components/common/Modal";
import { getCurrentUser, isSuperAdmin, isAdmin } from "../../../core/utils/auth";

// --- SVG Icons ---
const MegaphoneIcon = ({ className }) => (
  <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const PlusIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);
const PencilSquareIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
  </svg>
);
const Cog6ToothIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />  </svg>
);
const MagnifyingGlassIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
  </svg>
);
const EyeIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);
const TrashIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
  </svg>
);
const PaperAirplaneIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
  </svg>
);
const ArrowPathIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);
const ChevronLeftIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);
const ChevronRightIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);
const StopCircleIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);
const ServerStackIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.75 5.1a3 3 0 0 1 2.4-1.35h7.7a3 3 0 0 1 2.4 1.35l2.1 3.45a4.5 4.5 0 0 1 .9 2.7" />
  </svg>
);
const PhoneIcon = ({ className = "w-3 h-3" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" />
  </svg>
);

export default function CampaignManagement() {
  const navigate = useNavigate();

  const currentUser = useMemo(() => getCurrentUser(), []);
  const showCredits = useMemo(() => {
    if (!currentUser) return true;
    if (isSuperAdmin(currentUser) || isAdmin(currentUser)) return true;
    return currentUser.bool_show_credits !== false;
  }, [currentUser]);

  // Role Permission: User and Agent roles cannot delete campaigns (Admin/SuperAdmin only)
  const canDeleteCampaign = useMemo(() => {
    if (!currentUser) return false;
    return isSuperAdmin(currentUser) || isAdmin(currentUser);
  }, [currentUser]);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [serverStats, setServerStats] = useState(null);

  // Delivery breakdown / Message log modal states
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [campaignMessages, setCampaignMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [messageSearch, setMessageSearch] = useState("");

  // Batch Settings modal states
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [activeBatches, setActiveBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchTab, setBatchTab] = useState("parameters"); // "parameters" | "distribution" | "worker" | "queue"
  const [previewSeed, setPreviewSeed] = useState(0);

  const [batchSettings, setBatchSettings] = useState(() => {
    try {
      const saved = localStorage.getItem("whatsapp_batch_settings");
      const parsed = saved ? JSON.parse(saved) : {};
      const standardBatchSizes = [50, 100, 200, 500, 1000];
      const standardCooling = [5, 10, 30, 60, 120];
      const bSize = parsed.batchSize != null ? parsed.batchSize : 100;
      const cCool = parsed.coolingOffSeconds != null ? parsed.coolingOffSeconds : 10;
      return {
        batchProcessingMode: parsed.batchProcessingMode || "SYNCHRONOUS",
        senderSelectionMode: parsed.senderSelectionMode || "MULTIPLE",
        senderDistributionMode: parsed.senderDistributionMode || "SAME",
        batchSize: standardBatchSizes.includes(bSize) ? bSize : "custom",
        customBatchSize: bSize,
        coolingOffSeconds: standardCooling.includes(cCool) ? cCool : "custom",
        customCoolingOffSeconds: cCool,
        throttleDelaySeconds: parsed.throttleDelaySeconds != null ? parsed.throttleDelaySeconds : 10,
        rateLimitPerSecond: parsed.rateLimitPerSecond != null ? parsed.rateLimitPerSecond : 50,
        minMessagesPerMinute: parsed.minMessagesPerMinute != null ? parsed.minMessagesPerMinute : 40,
        maxMessagesPerMinute: parsed.maxMessagesPerMinute != null ? parsed.maxMessagesPerMinute : 60,
        autoRetryFailed: parsed.autoRetryFailed !== false,
        maxRetries: parsed.maxRetries != null ? parsed.maxRetries : 3,
        overrideWorkerSettings: parsed.overrideWorkerSettings || false,
        customConcurrentWorkers: parsed.customConcurrentWorkers || 3,
        customPrefetch: parsed.customPrefetch || 10,
      };
    } catch (_) {
      return {
        batchProcessingMode: "SYNCHRONOUS",
        senderSelectionMode: "MULTIPLE",
        senderDistributionMode: "SAME",
        batchSize: 100,
        customBatchSize: 100,
        coolingOffSeconds: 10,
        customCoolingOffSeconds: 10,
        throttleDelaySeconds: 10,
        rateLimitPerSecond: 50,
        minMessagesPerMinute: 40,
        maxMessagesPerMinute: 60,
        autoRetryFailed: true,
        maxRetries: 3,
        overrideWorkerSettings: false,
        customConcurrentWorkers: 3,
        customPrefetch: 10,
      };
    }
  });

  // Sync batch settings from server on mount
  useEffect(() => {
    campaignService
      .getBatchSettings()
      .then((res) => {
        const remote = res?.data || res;
        if (remote && typeof remote === "object") {
          const standardBatchSizes = [50, 100, 200, 500, 1000];
          const standardCooling = [5, 10, 30, 60, 120];
          const bSize = remote.batchSize != null ? remote.batchSize : 100;
          const cCool = remote.coolingOffSeconds != null ? remote.coolingOffSeconds : 10;
          setBatchSettings((prev) => ({
            ...prev,
            ...remote,
            batchSize: standardBatchSizes.includes(bSize) ? bSize : "custom",
            customBatchSize: bSize,
            coolingOffSeconds: standardCooling.includes(cCool) ? cCool : "custom",
            customCoolingOffSeconds: cCool,
          }));
        }
      })
      .catch(() => {});
  }, []);

  const loadBatches = useCallback(async () => {
    setLoadingBatches(true);
    try {
      const res = await campaignService.getBatches({ limit: 50 });
      const list = res?.data || (Array.isArray(res) ? res : []);
      setActiveBatches(list);
    } catch (err) {
      console.error("Failed to load batches:", err);
      setActiveBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  }, []);

  const handleSaveBatchSettings = async () => {
    try {
      const effectiveBatchSize =
        batchSettings.batchSize === "custom"
          ? Math.max(1, Number(batchSettings.customBatchSize) || 100)
          : Number(batchSettings.batchSize);

      const effectiveCooling =
        batchSettings.coolingOffSeconds === "custom"
          ? Math.max(1, Number(batchSettings.customCoolingOffSeconds) || 10)
          : Number(batchSettings.coolingOffSeconds);

      const payload = {
        ...batchSettings,
        batchSize: effectiveBatchSize,
        coolingOffSeconds: effectiveCooling,
        autoRetry429: batchSettings.autoRetryFailed !== false,
      };

      localStorage.setItem("whatsapp_batch_settings", JSON.stringify(payload));
      await campaignService.saveBatchSettings(payload).catch(() => {});

      Swal.fire({
        icon: "success",
        title: "Batch Settings Saved",
        text: "Your broadcast batch sizes, distribution mode, and throttle intervals have been updated.",
        timer: 2000,
        showConfirmButton: false,
      });
      setIsBatchModalOpen(false);
    } catch (_) {}
  };

  const simulatorItems = useMemo(() => {
    const senders = ["Num 1", "Num 2", "Num 3", "Num 4", "Num 5"];
    if (batchSettings.senderDistributionMode === "SAME") {
      const base = Math.floor(100 / 5);
      return senders.map((name) => ({ name, count: base }));
    } else {
      const weights = senders.map((_, i) => {
        const rand = Math.sin((previewSeed + 1) * 31.7 + (i + 1) * 17.3 + 123.45);
        return 0.4 + Math.abs(rand - Math.floor(rand)) * 1.8;
      });
      const sumW = weights.reduce((a, b) => a + b, 0);
      const raw = weights.map((w) => (w / sumW) * 100);
      const counts = raw.map((r) => Math.max(1, Math.floor(r)));
      let currentSum = counts.reduce((a, b) => a + b, 0);
      let diff = 100 - currentSum;
      const remainders = raw.map((r, i) => ({ i, rem: r - counts[i] })).sort((a, b) => b.rem - a.rem);
      for (let i = 0; i < diff; i++) counts[remainders[i % 5].i]++;
      return senders.map((name, i) => ({ name, count: counts[i] }));
    }
  }, [batchSettings.senderDistributionMode, previewSeed]);

  // Load Campaigns
  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const fetchFn = (campaignService.getCampaigns || campaignService.get).bind(campaignService);
      const res = await fetchFn({
        search: search.trim() || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        page: currentPage,
        limit: pageSize,
      });
      if (res && res.success && Array.isArray(res.data)) {
        setCampaigns(res.data);
        if (res.pagination) {
          setPagination(res.pagination);
        } else {
          setPagination({
            total: res.data.length,
            page: currentPage,
            limit: pageSize,
            totalPages: Math.ceil(res.data.length / pageSize) || 1,
          });
        }
        if (res.stats) {
          setServerStats(res.stats);
        }
      } else if (Array.isArray(res)) {
        setCampaigns(res);
        setPagination({
          total: res.length,
          page: currentPage,
          limit: pageSize,
          totalPages: Math.ceil(res.length / pageSize) || 1,
        });
      } else if (res && Array.isArray(res.data)) {
        setCampaigns(res.data);
        setPagination({
          total: res.total || res.data.length,
          page: currentPage,
          limit: pageSize,
          totalPages: Math.ceil((res.total || res.data.length) / pageSize) || 1,
        });
      } else {
        setCampaigns([]);
        setPagination({ total: 0, page: 1, limit: pageSize, totalPages: 1 });
      }
    } catch (err) {
      console.error("Failed to load campaigns:", err);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, currentPage, pageSize]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Statistics calculation
  const stats = useMemo(() => {
    if (serverStats) {
      return {
        totalCampaigns: serverStats.totalCampaigns ?? pagination.total ?? campaigns.length,
        totalMessages: serverStats.totalMessages ?? 0,
        sent: serverStats.sent ?? 0,
        delivered: serverStats.delivered ?? 0,
        read: serverStats.read ?? 0,
        failed: serverStats.failed ?? 0,
      };
    }

    let totalMessages = 0;
    let sent = 0;
    let delivered = 0;
    let read = 0;
    let failed = 0;

    campaigns.forEach((c) => {
      totalMessages += c.int_recipient_count || c.int_message_count || 0;
      sent += c.int_sent_count || 0;
      delivered += c.int_delivered_count || 0;
      read += c.int_read_count || 0;
      failed += c.int_failed_count || 0;
    });

    return {
      totalCampaigns: pagination.total || campaigns.length,
      totalMessages,
      sent,
      delivered,
      read,
      failed,
    };
  }, [serverStats, campaigns, pagination.total]);

  const totalRecords = pagination?.total ?? campaigns.length;
  const totalPages = pagination?.totalPages || Math.ceil(totalRecords / pageSize) || 1;

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  // Launch existing draft campaign
  const handleSendExisting = async (campaign) => {
    const result = await Swal.fire({
      title: "Launch Broadcast Campaign?",
      html: `Are you sure you want to send <b>${campaign.str_campaign_name}</b> to <b>${campaign.int_recipient_count || 0}</b> recipients now?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send Now! 🚀",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#2563eb",
    });

    if (!result.isConfirmed) return;

    Swal.fire({
      title: "Dispatching Messages...",
      text: "Sending messages through the WhatsApp gateway...",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const id = campaign.int_campaign_id || campaign._id;
      const res = await campaignService.sendCampaign(id);
      if (res && res.success) {
        await Swal.fire({
          icon: "success",
          title: "Broadcast Initiated!",
          text: res.message || "Messages are being delivered.",
          timer: 2000,
          showConfirmButton: false,
        });
        loadCampaigns();
      } else {
        Swal.fire({
          icon: "error",
          title: "Dispatch Failed",
          text: res?.error || "Could not dispatch campaign.",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.error || err.message,
      });
    }
  };

  // Cancel Running/Pending/Draft Campaign (Requirement 27)
  const handleCancelCampaign = async (campaign) => {
    const id = campaign.int_campaign_id || campaign._id;
    const isDraft = (campaign.str_status || "").toUpperCase() === "DRAFT";
    const result = await Swal.fire({
      title: isDraft ? "Cancel Draft Campaign?" : "Cancel Broadcast Campaign?",
      text: isDraft
        ? `Are you sure you want to cancel the draft campaign "${campaign.str_campaign_name || "Campaign #" + id}"? Its status will be changed to CANCELLED.`
        : `Are you sure you want to cancel "${campaign.str_campaign_name || "Campaign #" + id}"? Pending RabbitMQ jobs will be stopped immediately. Currently processing messages will complete safely, and remaining recipients will be marked as CANCELLED.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: isDraft ? "Yes, Cancel Draft" : "Yes, Cancel Broadcast",
      cancelButtonText: isDraft ? "Keep Draft" : "Keep Running",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await campaignService.cancelCampaign(id);
      if (res && (res.success || !res.error)) {
        await Swal.fire({
          icon: "success",
          title: "Broadcast Cancelled",
          text: res.message || "Campaign halted and pending jobs cancelled.",
          timer: 2000,
          showConfirmButton: false,
        });
        loadCampaigns();
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Cancellation Error",
        text: err.response?.data?.error || err.message,
      });
    }
  };

  // Delete Campaign
  const handleDeleteCampaign = async (campaign) => {
    const id = campaign.int_campaign_id || campaign._id;
    const result = await Swal.fire({
      title: "Delete Campaign?",
      text: `Are you sure you want to delete "${campaign.str_campaign_name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await campaignService.delete(id);
      if (res && (res.success || !res.error)) {
        await Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Campaign deleted successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
        loadCampaigns();
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Delete Error", text: err.message });
    }
  };

  // View Delivery Breakdown & Messages
  const handleOpenDeliveryLog = async (campaign) => {
    setActiveCampaign(campaign);
    setIsLogModalOpen(true);
    setLoadingMessages(true);

    try {
      const id = campaign.int_campaign_id || campaign._id;
      const res = await campaignService.getCampaignMessages(id, { limit: 100 });
      if (res && res.success) {
        setCampaignMessages(res.data || []);
      }
    } catch (err) {
      console.error("Failed to load campaign messages:", err);
    } finally {
      setLoadingMessages(false);
    }
  };

  // Retry Failed Recipients
  const handleRetryFailed = async () => {
    if (!activeCampaign) return;
    const id = activeCampaign.int_campaign_id || activeCampaign._id;

    try {
      const res = await campaignService.retryFailed(id);
      if (res && res.success) {
        Swal.fire({
          icon: "success",
          title: "Retry Triggered",
          text: res.message || "Failed messages are being re-sent.",
          timer: 2000,
          showConfirmButton: false,
        });
        handleOpenDeliveryLog(activeCampaign);
        loadCampaigns();
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Retry Failed", text: err.message });
    }
  };

  // Filtered campaign messages in modal
  const filteredMessages = useMemo(() => {
    if (!messageSearch.trim()) return campaignMessages;
    const q = messageSearch.toLowerCase().trim();
    return campaignMessages.filter((m) => {
      return (
        (m.str_recipient_number || "").includes(q) ||
        (m.str_status || "").toLowerCase().includes(q) ||
        (m.str_operator_message_id || "").toLowerCase().includes(q)
      );
    });
  }, [campaignMessages, messageSearch]);

  return (
    <div className="space-y-6">
      {/* ── Top Blue Banner (Matching Reference Design) ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-xs flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/20">
          <MegaphoneIcon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-gray-900 dark:text-white">
            WhatsApp Broadcast Campaigns
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
            Create, schedule, and execute high-throughput WhatsApp marketing and utility campaigns. Batches and delivery breakdown logs are stored directly in your private tenant database.
          </p>
        </div>
      </div>

      {/* ── Key Metrics Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
            Total Campaigns
          </span>
          <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {stats.totalCampaigns}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
            Total Recipients
          </span>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
            {stats.totalMessages}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
          <span className="text-[11px] font-semibold text-emerald-600 uppercase tracking-wider">
            Sent Messages
          </span>
          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
            {stats.sent}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
          <span className="text-[11px] font-semibold text-purple-600 uppercase tracking-wider">
            Delivered
          </span>
          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
            {stats.delivered}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs">
          <span className="text-[11px] font-semibold text-rose-500 uppercase tracking-wider">
            Failed
          </span>
          <div className="text-2xl font-bold text-rose-500 dark:text-rose-400 mt-1">
            {stats.failed}
          </div>
        </div>
      </div>

      {/* ── Main Data Card ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xs overflow-hidden">
        {/* Table Header / Action Toolbar */}
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                Broadcast Campaigns
              </h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-semibold">
                {totalRecords} records
              </span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">Real-time synchronized data</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Search Input */}
            <div className="relative w-full sm:w-64">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search table..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-hidden text-gray-900 dark:text-white"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="py-2 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-hidden text-gray-800 dark:text-gray-200"
            >
              <option value="all">All Status</option>
              <option value="COMPLETED">Completed</option>
              <option value="PROCESSING">Processing</option>
              <option value="DRAFT">Draft</option>
              <option value="FAILED">Failed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>

            {/* Batch Setting Button */}
            <button
              type="button"
              onClick={() => {
                setIsBatchModalOpen(true);
                loadBatches();
              }}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl text-xs transition-colors cursor-pointer border border-gray-200 dark:border-gray-600"
              title="Configure broadcast batch dispatch and throttling"
            >
              <Cog6ToothIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <span>Batch Setting</span>
            </button>

            {/* + Add New Button (Navigates to Dedicated Create Page) */}
            <button
              onClick={() => navigate("/workspace/campaigns/create")}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl text-xs shadow-md shadow-blue-500/20 transition-all cursor-pointer"
            >
              <PlusIcon className="w-4 h-4" />
              <span>Add New</span>
            </button>
          </div>
        </div>

        {/* Campaigns Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-16 text-center">
              <span className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
              <p className="mt-2 text-xs font-medium text-gray-500">Loading campaigns...</p>
            </div>
          ) : campaigns.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-center">
              <div className="text-4xl mb-3">📁</div>
              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">No records found</p>
              <p className="text-xs text-gray-400 mt-1 max-w-sm">
                You haven't launched any broadcast campaigns yet. Click "+ Add New" to start your first campaign.
              </p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  <th className="py-3 px-4">Campaign Title</th>
                  <th className="py-3 px-4">Template</th>
                  <th className="py-3 px-4">Sender Number</th>
                  <th className="py-3 px-4 text-center">Audience Size</th>
                  <th className="py-3 px-4">Delivery Progress</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {campaigns.map((camp) => {
                  const total = camp.int_recipient_count || camp.int_message_count || 0;
                  const sent = camp.int_sent_count || 0;
                  const delivered = camp.int_delivered_count || 0;
                  const failed = camp.int_failed_count || 0;
                  const pct = total > 0 ? Math.min(100, Math.round(((sent + delivered) / total) * 100)) : 0;

                  const status = (camp.str_status || "DRAFT").toUpperCase();
                  const statusColors = {
                    COMPLETED: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200",
                    PROCESSING: "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200 animate-pulse",
                    DRAFT: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-300",
                    FAILED: "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-200",
                    SCHEDULED: "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200",
                    CANCELLED: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 border-red-200",
                  };

                  return (
                    <tr
                      key={camp._id || camp.int_campaign_id}
                      className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      {/* Campaign Title */}
                      <td className="py-3.5 px-4 font-semibold text-gray-900 dark:text-white">
                        <div className="flex flex-col">
                          <span className="font-bold text-xs">{camp.str_campaign_name}</span>
                          <span className="text-[10px] text-gray-400 font-mono">
                            ID: {camp.str_campaign_id || `#${camp.int_campaign_id}`}
                          </span>
                        </div>
                      </td>

                      {/* Template */}
                      <td className="py-3.5 px-4">
                        <span className="px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          {camp.str_template_name || "-"}
                        </span>
                      </td>

                      {/* Sender Number */}
                      <td className="py-3.5 px-4">
                        {(() => {
                          const isMulti =
                            (camp.str_sender_selection_mode || "").toUpperCase() === "MULTIPLE" ||
                            (Array.isArray(camp.arr_sender_numbers) && camp.arr_sender_numbers.length > 1);
                          const senders = Array.isArray(camp.arr_sender_numbers) && camp.arr_sender_numbers.length > 0
                            ? camp.arr_sender_numbers
                            : (camp.str_sender_number ? [camp.str_sender_number] : (camp.str_phone_number_id ? [camp.str_phone_number_id] : []));

                          if (isMulti && senders.length > 1) {
                            const isDiff = (camp.str_sender_distribution_mode || "").toUpperCase() === "DIFFERENT";
                            const formatted = senders.map((s) => `+${String(s).replace(/^\+/, "")}`);
                            return (
                              <div className="flex flex-col gap-1 items-start">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                                    <PhoneIcon className="w-2.5 h-2.5" />
                                    {senders.length} Numbers
                                  </span>
                                  <span
                                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border ${
                                      isDiff
                                        ? "bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 border-purple-200 dark:border-purple-800"
                                        : "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border-blue-200 dark:border-blue-800"
                                    }`}
                                  >
                                    {isDiff ? "Diff Dist." : "Same Dist."}
                                  </span>
                                </div>
                                <div
                                  className="text-[11px] font-mono text-gray-700 dark:text-gray-300 max-w-[210px] truncate cursor-help"
                                  title={`Distributed across ${senders.length} senders (${isDiff ? "Different Distribution" : "Same Distribution"}):\n${formatted.join("\n")}`}
                                >
                                  {formatted.slice(0, 2).join(", ")}
                                  {formatted.length > 2 && (
                                    <span className="text-[10px] text-gray-400 dark:text-gray-400 ml-1 font-sans">
                                      +{formatted.length - 2} more
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          }

                          const single = senders[0] || camp.str_sender_number || camp.str_phone_number_id;
                          return single ? (
                            <span className="font-mono text-gray-700 dark:text-gray-300 text-xs">
                              +{String(single).replace(/^\+/, "")}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          );
                        })()}
                      </td>

                      {/* Audience Size */}
                      <td className="py-3.5 px-4 text-center font-bold text-gray-800 dark:text-gray-200">
                        <div>{total}</div>
                        {showCredits && (camp.dbl_total_cost > 0 || camp.dbl_estimated_cost > 0) && (
                          <div className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold font-mono mt-0.5">
                            ₹{Number(camp.dbl_total_cost || camp.dbl_estimated_cost || 0).toFixed(2)}
                          </div>
                        )}
                      </td>

                      {/* Delivery Progress */}
                      <td className="py-3.5 px-4">
                        <div className="w-36">
                          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                            <span>{sent + delivered} sent</span>
                            <span>{pct}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
                            <div
                              style={{ width: `${pct}%` }}
                              className={`h-full ${status === "FAILED" ? "bg-rose-500" : "bg-blue-600"}`}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${
                            statusColors[status] || statusColors.DRAFT
                          }`}
                        >
                          {status}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* View Campaign Report */}
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/workspace/reports/campaigns?campaignId=${
                                  camp.int_campaign_id || camp._id || camp.str_campaign_id
                                }`
                              )
                            }
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors cursor-pointer"
                            title="View Campaign Report"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>

                          {/* Edit Draft Campaign */}
                          {status === "DRAFT" && (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/workspace/campaigns/create?edit=${camp.int_campaign_id || camp._id}`
                                )
                              }
                              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors cursor-pointer"
                              title="Edit Draft Campaign"
                            >
                              <PencilSquareIcon className="w-4 h-4" />
                            </button>
                          )}

                          {/* Send / Dispatch (if draft) */}
                          {status === "DRAFT" && (
                            <button
                              type="button"
                              onClick={() => handleSendExisting(camp)}
                              className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded-lg transition-colors cursor-pointer"
                              title="Send Campaign Now"
                            >
                              <PaperAirplaneIcon className="w-4 h-4" />
                            </button>
                          )}

                          {/* Cancel running/pending/draft campaign */}
                          {(status === "PROCESSING" || status === "SCHEDULED" || status === "PENDING" || status === "DRAFT") && (
                            <button
                              type="button"
                              onClick={() => handleCancelCampaign(camp)}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors cursor-pointer"
                              title={status === "DRAFT" ? "Cancel Draft Campaign" : "Cancel Broadcast"}
                            >
                              <StopCircleIcon className="w-4 h-4" />
                            </button>
                          )}

                          {/* Delete (Admin/SuperAdmin only - User & Agent roles cannot delete) */}
                          {canDeleteCampaign && (
                            <button
                              type="button"
                              onClick={() => handleDeleteCampaign(camp)}
                              className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors cursor-pointer"
                              title="Delete Campaign"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Table Pagination Footer ── */}
        {!loading && totalRecords > 0 && (
          <div className="p-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/40 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
            {/* Left: Showing entries & Page Size selector */}
            <div className="flex flex-wrap items-center gap-3 text-gray-500 dark:text-gray-400">
              <span>
                Showing{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {(currentPage - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {Math.min(currentPage * pageSize, totalRecords)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {totalRecords}
                </span>{" "}
                records
              </span>

              <div className="flex items-center gap-1.5 pl-2 border-l border-gray-200 dark:border-gray-700">
                <span className="text-[11px] text-gray-400">Per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="py-1 px-2 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-hidden text-gray-800 dark:text-gray-200 cursor-pointer shadow-xs"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            {/* Right: Page Navigation Buttons */}
            <div className="flex items-center gap-1">
              {/* First Page */}
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-medium cursor-pointer"
                title="First Page"
              >
                «
              </button>

              {/* Previous Page */}
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Previous Page"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>

              {/* Page Number Buttons */}
              {pageNumbers.map((page, idx) => {
                if (page === "...") {
                  return (
                    <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 select-none">
                      ...
                    </span>
                  );
                }
                return (
                  <button
                    key={page}
                    type="button"
                    onClick={() => setCurrentPage(page)}
                    className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      currentPage === page
                        ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              {/* Next Page */}
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                title="Next Page"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>

              {/* Last Page */}
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors text-xs font-medium cursor-pointer"
                title="Last Page"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── DELIVERY BREAKDOWN & RECIPIENTS LOG MODAL ── */}
      <Modal
        isOpen={isLogModalOpen}
        onClose={() => setIsLogModalOpen(false)}
        title={activeCampaign ? `Delivery Breakdown: ${activeCampaign.str_campaign_name}` : "Delivery Breakdown"}
        maxWidth="max-w-4xl"
      >
        <div className="space-y-4">
          {activeCampaign && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
                <span className="text-[10px] text-gray-400 font-bold uppercase">Total</span>
                <div className="text-base font-bold text-gray-900 dark:text-white mt-0.5">
                  {activeCampaign.int_recipient_count || activeCampaign.int_message_count || 0}
                </div>
              </div>
              <div className="p-3 bg-emerald-50/60 dark:bg-emerald-950/30 rounded-xl border border-emerald-200 dark:border-emerald-800/40 text-center">
                <span className="text-[10px] text-emerald-700 font-bold uppercase">Sent</span>
                <div className="text-base font-bold text-emerald-700 dark:text-emerald-400 mt-0.5">
                  {activeCampaign.int_sent_count || 0}
                </div>
              </div>
              <div className="p-3 bg-purple-50/60 dark:bg-purple-950/30 rounded-xl border border-purple-200 dark:border-purple-800/40 text-center">
                <span className="text-[10px] text-purple-700 font-bold uppercase">Delivered</span>
                <div className="text-base font-bold text-purple-700 dark:text-purple-400 mt-0.5">
                  {activeCampaign.int_delivered_count || 0}
                </div>
              </div>
              <div className="p-3 bg-rose-50/60 dark:bg-rose-950/30 rounded-xl border border-rose-200 dark:border-rose-800/40 text-center">
                <span className="text-[10px] text-rose-700 font-bold uppercase">Failed</span>
                <div className="text-base font-bold text-rose-700 dark:text-rose-400 mt-0.5">
                  {activeCampaign.int_failed_count || 0}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 pt-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search recipient number or status..."
                value={messageSearch}
                onChange={(e) => setMessageSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl"
              />
            </div>

            {activeCampaign && (activeCampaign.int_failed_count > 0 || campaignMessages.some((m) => m.str_status === "FAILED")) && (
              <button
                type="button"
                onClick={handleRetryFailed}
                className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                Retry Failed
              </button>
            )}
          </div>

          <div className="overflow-x-auto max-h-80 border border-gray-200 dark:border-gray-700 rounded-xl">
            {loadingMessages ? (
              <div className="py-12 text-center text-xs text-gray-400">Loading delivery breakdown...</div>
            ) : filteredMessages.length === 0 ? (
              <div className="py-12 text-center text-xs text-gray-400">No message records for this campaign.</div>
            ) : (
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-[10px] font-bold uppercase text-gray-400">
                  <tr>
                    <th className="py-2.5 px-3">Recipient Number</th>
                    <th className="py-2.5 px-3">Status</th>
                    <th className="py-2.5 px-3">Operator Message ID</th>
                    <th className="py-2.5 px-3">Sent Time</th>
                    <th className="py-2.5 px-3">Error / Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredMessages.map((msg) => (
                    <tr key={msg._id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40">
                      <td className="py-2 px-3 font-mono font-bold text-gray-900 dark:text-white">
                        {msg.str_recipient_number}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                            msg.str_status === "DELIVERED"
                              ? "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300"
                              : msg.str_status === "SENT"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                              : msg.str_status === "FAILED"
                              ? "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          }`}
                        >
                          {msg.str_status}
                        </span>
                      </td>
                      <td className="py-2 px-3 font-mono text-[11px] text-gray-500 truncate max-w-xs">
                        {msg.str_operator_message_id || "-"}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-gray-500">
                        {msg.dttm_sent_on ? new Date(msg.dttm_sent_on).toLocaleString() : "-"}
                      </td>
                      <td className="py-2 px-3 text-[11px] text-rose-500 truncate max-w-xs">
                        {msg.str_error_message || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>

      {/* ── BATCH SETTINGS & QUEUE MODAL (Requirement 34) ── */}
      <Modal
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        title="Broadcast Batch & Throttle Settings"
        maxWidth="max-w-3xl"
      >
        <div className="space-y-5">
          {/* Modal Tab Switcher (Requirement 34) */}
          <div className="flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700 pb-2.5 overflow-x-auto text-xs">
            {[
              { id: "parameters", label: "Batch Parameters" },
              { id: "distribution", label: "Sender Distribution" },
              { id: "worker", label: "Worker Settings" },
              { id: "queue", label: `Active Queue (${activeBatches.length})` },
            ].map((tab) => {
              const isActive = batchTab === tab.id || (tab.id === "parameters" && batchTab === "settings");
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setBatchTab(tab.id);
                    if (tab.id === "queue") loadBatches();
                  }}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    isActive
                      ? "bg-blue-600 text-white shadow-xs"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* TAB 1: BATCH PARAMETERS */}
          {(batchTab === "parameters" || batchTab === "settings") && (
            <div className="space-y-4.5 max-h-[75vh] overflow-y-auto pr-1">
              {/* 8. Batch Processing Mode */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900/60 rounded-2xl border border-gray-200 dark:border-gray-700/80 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      Batch Processing Mode
                    </h3>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                      Configure how campaign batches are sequenced through worker dispatchers.
                    </p>
                  </div>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold">
                    24h Quota Enforced
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Synchronous Radio Card */}
                  <label
                    className={`relative p-3.5 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${
                      batchSettings.batchProcessingMode === "SYNCHRONOUS"
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 shadow-xs ring-1 ring-blue-600"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="batchProcessingMode"
                        value="SYNCHRONOUS"
                        checked={batchSettings.batchProcessingMode === "SYNCHRONOUS"}
                        onChange={() =>
                          setBatchSettings({ ...batchSettings, batchProcessingMode: "SYNCHRONOUS" })
                        }
                        className="mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <div className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                          <span>Synchronous</span>
                          <span className="text-[9px] px-1.5 py-0.2 bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 rounded font-semibold">
                            Recommended
                          </span>
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          The next batch starts only after the previous batch has completed according to cooling rules.
                        </p>
                      </div>
                    </div>
                    <div className="mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700/60 font-mono text-[9px] text-gray-400 flex items-center gap-1">
                      <span>Batch 1</span> → <span>Complete</span> → <span>Cooling</span> → <span>Batch 2</span>
                    </div>
                  </label>

                  {/* Asynchronous Radio Card */}
                  <label
                    className={`relative p-3.5 rounded-xl border cursor-pointer transition-all flex flex-col justify-between ${
                      batchSettings.batchProcessingMode === "ASYNCHRONOUS"
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 shadow-xs ring-1 ring-blue-600"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="batchProcessingMode"
                        value="ASYNCHRONOUS"
                        checked={batchSettings.batchProcessingMode === "ASYNCHRONOUS"}
                        onChange={() =>
                          setBatchSettings({ ...batchSettings, batchProcessingMode: "ASYNCHRONOUS" })
                        }
                        className="mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <div className="text-xs font-bold text-gray-900 dark:text-white">
                          Asynchronous
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          Multiple batches processed concurrently bounded by concurrency, rate limit, and queue limits.
                        </p>
                      </div>
                    </div>
                    <div className="mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700/60 text-[10px] text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                      <span>🔒 Strictly respects user 24h limit</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Batch Sizing, Cooling-off Period & Throttle Delay */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Batch Size */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Batch Size (Messages / Chunk)
                  </label>
                  <select
                    value={batchSettings.batchSize}
                    onChange={(e) => {
                      const val = e.target.value === "custom" ? "custom" : Number(e.target.value);
                      setBatchSettings({ ...batchSettings, batchSize: val });
                    }}
                    className="w-full py-2 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  >
                    <option value={50}>50 messages per batch</option>
                    <option value={100}>100 messages per batch (Default / Recommended)</option>
                    <option value={200}>200 messages per batch</option>
                    <option value={500}>500 messages per batch</option>
                    <option value={1000}>1000 messages per batch (High throughput)</option>
                    <option value="custom">Custom Batch Size...</option>
                  </select>
                  {batchSettings.batchSize === "custom" && (
                    <input
                      type="number"
                      min={10}
                      max={10000}
                      placeholder="Enter custom batch size..."
                      value={batchSettings.customBatchSize}
                      onChange={(e) =>
                        setBatchSettings({
                          ...batchSettings,
                          customBatchSize: Math.max(1, Number(e.target.value) || 1),
                        })
                      }
                      className="w-full mt-1.5 py-1.5 px-3 text-xs bg-white dark:bg-gray-800 border border-blue-400 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">
                    Audiences are split into chunks of this size in `tbl_campaign_batches`.
                  </p>
                </div>

                {/* Cooling-Off Period Between Batches */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Cooling-Off Period Between Batches
                  </label>
                  <select
                    value={batchSettings.coolingOffSeconds}
                    onChange={(e) => {
                      const val = e.target.value === "custom" ? "custom" : Number(e.target.value);
                      setBatchSettings({ ...batchSettings, coolingOffSeconds: val });
                    }}
                    className="w-full py-2 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  >
                    <option value={5}>5 seconds</option>
                    <option value={10}>10 seconds (Default)</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>60 seconds (1 minute)</option>
                    <option value={120}>120 seconds (2 minutes)</option>
                    <option value="custom">Custom Period...</option>
                  </select>
                  {batchSettings.coolingOffSeconds === "custom" && (
                    <input
                      type="number"
                      min={1}
                      max={3600}
                      placeholder="Enter seconds..."
                      value={batchSettings.customCoolingOffSeconds}
                      onChange={(e) =>
                        setBatchSettings({
                          ...batchSettings,
                          customCoolingOffSeconds: Math.max(1, Number(e.target.value) || 1),
                        })
                      }
                      className="w-full mt-1.5 py-1.5 px-3 text-xs bg-white dark:bg-gray-800 border border-blue-400 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">
                    Pause duration between batch dispatches scheduled via queue/job scheduler.
                  </p>
                </div>

                {/* Throttle Delay Between Batches */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Throttle Delay Between Batches (Seconds)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={300}
                    value={batchSettings.throttleDelaySeconds}
                    onChange={(e) =>
                      setBatchSettings({
                        ...batchSettings,
                        throttleDelaySeconds: Math.max(1, Number(e.target.value) || 1),
                      })
                    }
                    className="w-full py-2 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Pause duration between batch dispatches to protect gateway quality rating.
                  </p>
                </div>

                {/* Minimum Messages / Minute */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Minimum Messages / Minute
                  </label>
                  <input
                    type="number"
                    min={5}
                    max={500}
                    value={batchSettings.minMessagesPerMinute}
                    onChange={(e) =>
                      setBatchSettings({
                        ...batchSettings,
                        minMessagesPerMinute: Math.max(1, Number(e.target.value) || 1),
                      })
                    }
                    className="w-full py-2 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Minimum floor sending rate for dispatch pacing.
                  </p>
                </div>

                {/* Maximum Messages / Minute */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Maximum Messages / Minute
                  </label>
                  <input
                    type="number"
                    min={5}
                    max={1000}
                    value={batchSettings.maxMessagesPerMinute}
                    onChange={(e) =>
                      setBatchSettings({
                        ...batchSettings,
                        maxMessagesPerMinute: Math.max(
                          Number(batchSettings.minMessagesPerMinute) || 1,
                          Number(e.target.value) || 1
                        ),
                      })
                    }
                    className="w-full py-2 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">
                    Maximum speed cap for dispatch pacing.
                  </p>
                </div>
              </div>

              {/* Rate Limits Priority Note (Requirement 35) */}
              <div className="p-3 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/50 rounded-xl text-[11px] text-blue-800 dark:text-blue-300 flex items-start gap-2.5">
                <span className="text-sm shrink-0">ℹ️</span>
                <span className="leading-relaxed">
                  <strong>Configuration Priority:</strong> Global Safety Limit → User 24h Limit → User Per-Minute Limit → Campaign Rate Limit → Sender Limit → Worker Concurrency → Batch Size. <strong>The most restrictive applicable limit always wins.</strong>
                </span>
              </div>

              {/* Retry Attempts & HTTP 429 Auto-Retry */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">
                    Retry Attempts on Carrier Failure
                  </label>
                  <select
                    value={batchSettings.maxRetries}
                    onChange={(e) =>
                      setBatchSettings({ ...batchSettings, maxRetries: Number(e.target.value) })
                    }
                    className="w-full py-2 px-3 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white"
                  >
                    <option value={1}>1 Retry</option>
                    <option value={2}>2 Retries</option>
                    <option value={3}>3 Retries (Default)</option>
                    <option value={5}>5 Retries</option>
                  </select>
                  <p className="text-[10px] text-gray-400 mt-1">
                    Applies to temporary errors (HTTP 429, timeouts, 5xx). Permanent errors fail immediately.
                  </p>
                </div>

                <div className="flex items-center justify-between p-3.5 bg-gray-50 dark:bg-gray-900/60 rounded-xl border border-gray-200 dark:border-gray-700 h-[74px]">
                  <div>
                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200 block">
                      Auto-Retry Rate Limited Messages (HTTP 429)
                    </span>
                    <span className="text-[11px] text-gray-400">
                      Reads `Retry-After` header & applies exponential backoff
                    </span>
                  </div>
                  <input
                    type="checkbox"
                    checked={batchSettings.autoRetryFailed}
                    onChange={(e) =>
                      setBatchSettings({ ...batchSettings, autoRetryFailed: e.target.checked })
                    }
                    className="w-4 h-4 text-blue-600 rounded-sm focus:ring-blue-500 cursor-pointer shrink-0"
                  />
                </div>
              </div>

              {/* Footer Save */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsBatchModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveBatchSettings}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs shadow-md shadow-blue-500/20 transition-all cursor-pointer"
                >
                  Save Batch Settings
                </button>
              </div>
            </div>
          )}

          {/* TAB 2: SENDER DISTRIBUTION (Requirement 34) */}
          {batchTab === "distribution" && (
            <div className="space-y-4.5 max-h-[75vh] overflow-y-auto pr-1">
              <div className="p-4 bg-gray-50 dark:bg-gray-900/60 rounded-2xl border border-gray-200 dark:border-gray-700/80 space-y-3">
                <div>
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                    Sender Selection Mode
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    Choose whether this broadcast routes traffic through a single sender or distributes across multiple phone numbers.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label
                    className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                      batchSettings.senderSelectionMode === "SINGLE"
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 shadow-xs ring-1 ring-blue-600"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="senderSelectionMode"
                        value="SINGLE"
                        checked={batchSettings.senderSelectionMode === "SINGLE"}
                        onChange={() =>
                          setBatchSettings({ ...batchSettings, senderSelectionMode: "SINGLE" })
                        }
                        className="mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <div className="text-xs font-bold text-gray-900 dark:text-white">
                          Single Sender Number
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          All message batches are dispatched through a single primary verified sender number.
                        </p>
                      </div>
                    </div>
                  </label>

                  <label
                    className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                      batchSettings.senderSelectionMode === "MULTIPLE"
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 shadow-xs ring-1 ring-blue-600"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="senderSelectionMode"
                        value="MULTIPLE"
                        checked={batchSettings.senderSelectionMode === "MULTIPLE"}
                        onChange={() =>
                          setBatchSettings({ ...batchSettings, senderSelectionMode: "MULTIPLE" })
                        }
                        className="mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <div className="text-xs font-bold text-gray-900 dark:text-white">
                          Multiple Sender Numbers
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          Traffic is split across selected sender numbers to maximize throughput and preserve sender health.
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Sender Distribution Mode */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900/60 rounded-2xl border border-gray-200 dark:border-gray-700/80 space-y-3">
                <div>
                  <h3 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                    Sender Allocation Distribution Mode
                  </h3>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    Configure how audience recipients are divided across verified sender numbers.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label
                    className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                      batchSettings.senderDistributionMode === "SAME"
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 shadow-xs ring-1 ring-blue-600"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="senderDistributionMode"
                        value="SAME"
                        checked={batchSettings.senderDistributionMode === "SAME"}
                        onChange={() =>
                          setBatchSettings({ ...batchSettings, senderDistributionMode: "SAME" })
                        }
                        className="mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <div className="text-xs font-bold text-gray-900 dark:text-white">
                          Same Distribution
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          Divided as evenly as possible. Remainder is distributed fairly across numbers (minimal difference).
                        </p>
                      </div>
                    </div>
                  </label>

                  <label
                    className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                      batchSettings.senderDistributionMode === "DIFFERENT"
                        ? "border-blue-600 bg-blue-50/50 dark:bg-blue-950/20 shadow-xs ring-1 ring-blue-600"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="senderDistributionMode"
                        value="DIFFERENT"
                        checked={batchSettings.senderDistributionMode === "DIFFERENT"}
                        onChange={() =>
                          setBatchSettings({ ...batchSettings, senderDistributionMode: "DIFFERENT" })
                        }
                        className="mt-0.5 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <div>
                        <div className="text-xs font-bold text-gray-900 dark:text-white">
                          Different Distribution
                        </div>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                          Controlled randomized distribution varying batch to batch (e.g. Sender A: 7, Sender B: 10, Sender C: 17... Sum strictly equals recipients).
                        </p>
                      </div>
                    </div>
                  </label>
                </div>

                {/* Live Distribution Simulator Preview */}
                <div className="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700/80">
                  <div className="flex items-center justify-between text-[11px] mb-2">
                    <span className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                      <span>⚡ Distribution Simulator Preview</span>
                      <span className="font-normal text-gray-400">(100 msgs · 5 senders)</span>
                    </span>
                    {batchSettings.senderDistributionMode === "DIFFERENT" && (
                      <button
                        type="button"
                        onClick={() => setPreviewSeed((s) => s + 1)}
                        className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline font-bold cursor-pointer"
                      >
                        🎲 Re-roll Batch
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {simulatorItems.map((item, idx) => (
                      <div
                        key={idx}
                        className="p-2 rounded-lg bg-gray-50 dark:bg-gray-900/60 border border-gray-100 dark:border-gray-700/60 text-center"
                      >
                        <div className="text-[10px] text-gray-400 font-medium">{item.name}</div>
                        <div className="text-sm font-bold text-blue-600 dark:text-blue-400 font-mono mt-0.5">
                          {item.count}
                        </div>
                        <div className="text-[9px] text-gray-400">msgs</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-right text-[10px] text-emerald-600 dark:text-emerald-400 font-bold mt-1.5">
                    Total Assigned: 100 / 100 msgs (100% exact)
                  </div>
                </div>
              </div>

              {/* Footer Save */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsBatchModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveBatchSettings}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs shadow-md shadow-blue-500/20 transition-all cursor-pointer"
                >
                  Save Batch Settings
                </button>
              </div>
            </div>
          )}

          {/* TAB 3: WORKER SETTINGS (Requirement 34) */}
          {batchTab === "worker" && (
            <div className="space-y-4.5 max-h-[75vh] overflow-y-auto pr-1">
              <div className="p-3.5 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 rounded-2xl text-[11px] text-blue-800 dark:text-blue-300">
                ⚙️ <strong>User Queue Profile Sync:</strong> Worker settings are automatically read from your personal RabbitMQ queue profile. You operate on an isolated RabbitMQ queue with dynamic concurrency scaling.
              </div>

              {/* Worker Performance Parameters Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">QUEUE_NAME</span>
                  <div className="text-sm font-black font-mono text-indigo-600 dark:text-indigo-400 mt-0.5">
                    {currentUser?.str_username || "user"}_{currentUser?.int_user_id || 10001}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Dedicated RabbitMQ queue isolated to your account.</p>
                </div>

                <div className="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">CONCURRENT_WORKERS</span>
                  <div className="text-sm font-black font-mono text-blue-600 dark:text-blue-400 mt-0.5">
                    {batchSettings.overrideWorkerSettings ? (batchSettings.customConcurrentWorkers || 3) : 3} workers
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Parallel worker tasks allowed to consume jobs.</p>
                </div>

                <div className="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">WORKER_PREFETCH</span>
                  <div className="text-sm font-black font-mono text-emerald-600 dark:text-emerald-400 mt-0.5">
                    {batchSettings.overrideWorkerSettings ? (batchSettings.customPrefetch || 10) : 10} msgs
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Unacknowledged message delivery window per worker.</p>
                </div>

                <div className="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">MAX_IN_FLIGHT_MESSAGES</span>
                  <div className="text-sm font-black font-mono text-amber-600 dark:text-amber-400 mt-0.5">
                    {(batchSettings.overrideWorkerSettings ? (batchSettings.customConcurrentWorkers || 3) : 3) *
                      (batchSettings.overrideWorkerSettings ? (batchSettings.customPrefetch || 10) : 10)} msgs
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Worker Concurrency × Prefetch ceiling.</p>
                </div>

                <div className="p-3.5 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 sm:col-span-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">RATE_LIMIT_DELAY</span>
                  <div className="text-sm font-black font-mono text-purple-600 dark:text-purple-400 mt-0.5">
                    {Math.round(60000 / (Number(batchSettings.maxMessagesPerMinute) || 60))} ms
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Calculated dynamically from max messages per minute ({batchSettings.maxMessagesPerMinute || 60}/min).</p>
                </div>
              </div>

              {/* Campaign Override Controls */}
              <div className="p-4 bg-gray-50 dark:bg-gray-900/60 rounded-2xl border border-gray-200 dark:border-gray-700 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      Campaign Concurrency Override
                    </h4>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      Campaign-level settings override user queue defaults only when explicitly enabled.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={batchSettings.overrideWorkerSettings || false}
                      onChange={(e) =>
                        setBatchSettings({ ...batchSettings, overrideWorkerSettings: e.target.checked })
                      }
                      className="w-4 h-4 text-blue-600 rounded-sm focus:ring-blue-500 cursor-pointer"
                    />
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Override</span>
                  </label>
                </div>

                {batchSettings.overrideWorkerSettings ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Override Concurrent Workers (1–5)
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={5}
                        value={batchSettings.customConcurrentWorkers || 3}
                        onChange={(e) =>
                          setBatchSettings({
                            ...batchSettings,
                            customConcurrentWorkers: Math.max(1, Math.min(5, Number(e.target.value) || 1)),
                          })
                        }
                        className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border border-blue-400 rounded-xl focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Override Worker Prefetch (1–50)
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={batchSettings.customPrefetch || 10}
                        onChange={(e) =>
                          setBatchSettings({
                            ...batchSettings,
                            customPrefetch: Math.max(1, Math.min(50, Number(e.target.value) || 1)),
                          })
                        }
                        className="w-full px-3 py-2 text-xs bg-white dark:bg-gray-800 border border-blue-400 rounded-xl focus:ring-2 focus:ring-blue-500 font-mono"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 italic">
                    Currently utilizing User Queue defaults managed by the RabbitMQ Worker Daemon.
                  </div>
                )}
              </div>

              {/* Footer Save */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsBatchModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveBatchSettings}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs shadow-md shadow-blue-500/20 transition-all cursor-pointer"
                >
                  Save Batch Settings
                </button>
              </div>
            </div>
          )}

          {/* TAB 4: ACTIVE QUEUE */}
          {batchTab === "queue" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  Batches currently registered in `tbl_campaign_batches`
                </span>
                <button
                  type="button"
                  onClick={loadBatches}
                  className="text-xs text-blue-600 font-semibold hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <ArrowPathIcon className="w-3.5 h-3.5" />
                  <span>Refresh Queue</span>
                </button>
              </div>

              <div className="overflow-x-auto max-h-72 border border-gray-200 dark:border-gray-700 rounded-xl">
                {loadingBatches ? (
                  <div className="py-12 text-center text-xs text-gray-400">Loading batch queues...</div>
                ) : activeBatches.length === 0 ? (
                  <div className="py-12 text-center text-xs text-gray-400">
                    No active or pending batches in the queue.
                  </div>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-[10px] font-bold uppercase text-gray-400">
                      <tr>
                        <th className="py-2.5 px-3">Batch ID</th>
                        <th className="py-2.5 px-3">Campaign ID</th>
                        <th className="py-2.5 px-3">Message Count</th>
                        <th className="py-2.5 px-3">Status</th>
                        <th className="py-2.5 px-3">Created On</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700 font-medium">
                      {activeBatches.map((b) => (
                        <tr key={b._id || b.int_batch_id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/40">
                          <td className="py-2.5 px-3 font-mono font-bold text-gray-900 dark:text-white">
                            #{b.int_batch_id}
                          </td>
                          <td className="py-2.5 px-3 font-mono text-gray-600 dark:text-gray-300">
                            #{b.int_campaign_id}
                          </td>
                          <td className="py-2.5 px-3 font-bold text-gray-800 dark:text-gray-200">
                            {b.int_count || b.total_batch_msg_count || 0}
                          </td>
                          <td className="py-2.5 px-3">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                b.str_status === "COMPLETED"
                                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                                  : b.str_status === "PROCESSING"
                                  ? "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300"
                                  : "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                              }`}
                            >
                              {b.str_status || "PENDING"}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-gray-400 text-[11px]">
                            {b.dttm_created_on ? new Date(b.dttm_created_on).toLocaleString() : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
